import { Formik } from 'formik-2';
import PropTypes from 'prop-types';
import React from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import * as Yup from 'yup';
import { IconSizes } from '../../components/foundations/Icon';
import { Button, Label } from '../../components/widgets';
import Dialog from '../../components/widgets/MuiDialog';
import Spinner from '../../components/widgets/Spinner';
import { useFetchAgencies } from '../../dataSources/hooks';
import SingularLady from '../../resources/png/figures/singular_lady.png';
import { AutoCompleteFieldEnhanced, FormField } from '../../utils/components/formUtils';
import { translationPrefix as translationRootPrefix } from '../utils';
import css from './AgencyDialog.css';

const translationPrefix = `${translationRootPrefix}.AGENCY_DIALOG`;

const isAgencyAlreadyExists = (orgAgencies, agencyId) => {
    return orgAgencies.find(orgAgency => orgAgency.agency_id === agencyId);
};

const AgencyDialog = ({
    orgAgencies,
    agencySelectionForm,
    updateAgencySelectionForm,
    onCloseAgencyDialog,
    open,
    translate,
}) => {
    const { value: agencies, isLoading } = useFetchAgencies();
    const agencyOptions = agencies
        ? agencies.map(({ agency_id: value, display_name: label }) => {
              const exists = isAgencyAlreadyExists(orgAgencies, value);
              return {
                  value,
                  label,
                  extra: exists ? translate(`${translationPrefix}.AGENCY_ALREADY_EXISTS`) : '',
                  className: exists ? css.optionDisabled : '',
              };
          })
        : [];

    const initialAgency = agencyOptions.find(({ value }) => value === agencySelectionForm.id);

    const VALIDATION_SCHEMA = Yup.object().shape({
        agency: Yup.string()
            .required()
            .test('isAlreadyPermitted', '', agency => {
                if (!agency) return true;
                return !isAgencyAlreadyExists(orgAgencies, agency);
            }),
    });

    const onSubmit = ({ agency }) => {
        const agencyId = typeof agency !== 'object' ? agency : agency.value;
        const selectedAgencyOption = agencyOptions.find(({ value }) => value === agencyId);

        updateAgencySelectionForm({ id: selectedAgencyOption.value, name: selectedAgencyOption.label });
    };

    return (
        <Dialog title={`${translationPrefix}.HEADER`} paperClass={css.dialog} onClose={onCloseAgencyDialog} open={open}>
            <div className={css.content}>
                <img src={SingularLady} alt="Singular Lady" className={css.singularLady} />
                <div className={css.textContainer}>
                    <Label text={`${translationPrefix}.TITLE`} type="subtitle" className={css.title} />
                    <Label text={`${translationPrefix}.DESCRIPTION`} type="p" />
                </div>
                {isLoading ? (
                    <Spinner show={isLoading} />
                ) : (
                    <Formik initialValues={{ agency: initialAgency }} validationSchema={VALIDATION_SCHEMA}>
                        {formikProps => (
                            <>
                                <AgencySelectionContent
                                    agencyOptions={agencyOptions}
                                    orgAgencies={orgAgencies}
                                    {...formikProps}
                                />
                                <div className={css.actionButtonsContainer}>
                                    <Button type="secondary" onClick={onCloseAgencyDialog} className={css.actionButton}>
                                        <Translate id="STATIC.BUTTONS.CANCEL" />
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={() => onSubmit(formikProps.values)}
                                        Icon="chevron-right"
                                        iconLocation="right"
                                        iconSize={IconSizes.X_SMALL}
                                        className={css.actionButton}
                                        disabled={!formikProps.isValid || !formikProps.values.agency}
                                    >
                                        <Translate id="STATIC.BUTTONS.NEXT" />
                                    </Button>
                                </div>
                            </>
                        )}
                    </Formik>
                )}
            </div>
        </Dialog>
    );
};

AgencyDialog.propTypes = {
    orgAgencies: PropTypes.arrayOf(
        PropTypes.shape({
            agency_id: PropTypes.string,
            display_name: PropTypes.string,
        })
    ).isRequired,
    agencySelectionForm: PropTypes.objectOf(
        PropTypes.shape({
            id: PropTypes.string,
        })
    ).isRequired,
    updateAgencySelectionForm: PropTypes.func.isRequired,
    onCloseAgencyDialog: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
};

const AgencySelectionContent = ({ agencyOptions = [], initialValues, errors, touched }) => {
    return (
        <FormField className={css.formField}>
            <AutoCompleteFieldEnhanced
                name="agency"
                isMulti={false}
                options={agencyOptions}
                value={initialValues.agency}
                placeholder="STATIC.PLACEHOLDERS.SELECT_AGENCY"
                error={touched.agency && errors.agency}
                menuPlacement="bottom"
                selectOptions={{
                    maxMenuHeight: 150,
                }}
            />
        </FormField>
    );
};

AgencySelectionContent.propTypes = {
    agencyOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        })
    ),
    initialValues: PropTypes.objectOf(
        PropTypes.shape({
            agency: PropTypes.bool,
        })
    ),
    errors: PropTypes.objectOf(
        PropTypes.shape({
            agency: PropTypes.bool,
        })
    ),
    touched: PropTypes.objectOf(
        PropTypes.shape({
            agency: PropTypes.bool,
        })
    ),
};

export default withLocalize(AgencyDialog);
